import { useEffect, useRef, useState } from 'react';
import useAuth from './useAuth';

/**
 * Hook para Server-Sent Events (SSE)
 * Implementa reconexão automática e gerenciamento de estado
 * Seguindo padrões do projeto JBE Engenharia
 */
export const useSSE = (endpoint, options = {}) => {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const { auth } = useAuth();
  
  const {
    autoReconnect = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 5,
    onConnectionChange,
    onMessage,
    onError,
  } = options;
  
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const connect = () => {
    if (!auth?.user?.id || !endpoint) return;
    
    // Fecha conexão existente
    disconnect();
    
    try {
      const baseUrl = process.env.NODE_ENV === "development" 
        ? "http://localhost:8080" 
        : process.env.REACT_APP_BASE_URL;
      
      const url = `${baseUrl}${endpoint}?userId=${auth.user.id}`;
      
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        setConnected(true);
        setError(null);
        setReconnectAttempts(0);
        onConnectionChange?.(true);
        console.log(`SSE conectado: ${endpoint}`);
      };
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastEvent(data);
          
          // Adiciona à lista de notificações
          setNotifications(prev => [...prev, {
            id: Date.now() + Math.random(),
            data,
            timestamp: new Date()
          }]);
          
          onMessage?.(data);
        } catch (parseError) {
          console.warn('Erro ao fazer parse de evento SSE:', parseError);
        }
      };
      
      eventSource.onerror = (err) => {
        console.error('Erro SSE:', err);
        setConnected(false);
        setError(err);
        onError?.(err);
        onConnectionChange?.(false);
        
        // Auto-reconexão
        if (autoReconnect && reconnectAttempts < maxReconnectAttempts) {
          setReconnectAttempts(prev => prev + 1);
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Tentando reconectar SSE... (${reconnectAttempts + 1}/${maxReconnectAttempts})`);
            connect();
          }, reconnectDelay);
        }
      };
      
    } catch (err) {
      console.error('Erro ao criar conexão SSE:', err);
      setError(err);
      onError?.(err);
    }
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setConnected(false);
    onConnectionChange?.(false);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Conecta automaticamente quando deps mudam
  useEffect(() => {
    if (auth?.user?.id && endpoint) {
      connect();
    }
    
    return disconnect;
  }, [auth?.user?.id, endpoint]);

  // Cleanup na desmontagem
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    connected,
    lastEvent,
    error,
    notifications,
    reconnectAttempts,
    connect,
    disconnect,
    clearNotifications,
    removeNotification,
  };
};

/**
 * Hook específico para notificações WhatsApp
 */
export const useWhatsAppSSE = (connectionId) => {
  const endpoint = connectionId 
    ? `/api/notifications/connect?connectionId=${connectionId}`
    : '/api/notifications/connect';
    
  return useSSE(endpoint, {
    onMessage: (data) => {
      console.log('WhatsApp SSE Notification:', data);
    },
    onConnectionChange: (connected) => {
      console.log(`WhatsApp SSE ${connected ? 'conectado' : 'desconectado'}`);
    },
  });
};
